<template>
    <div>
        <input-field
            :class="marginClass"
            class="mb-5"
            :field="surgeryFields.diagnosis"
            field-name="diagnosis"
            v-model="diagnosis"
            :inline="false"
            bootstrap-native
        />
        <hr class="divider mb-5">

        <p class="text-center mx-auto mb-3" style="max-width: 30rem; font-size: 1.1rem;">
            {{ $t('surgery.selection.description') }}
        </p>

        <div class="mx-3 text-center" style="font-size: 1.3rem">
            <p class="mb-0">
                <b>
                    <label class="form-label">{{ $t('surgery.selection.selected') }}: </label>
                </b>
            </p>
            <p>
                <b>{{ textDescription }}</b>
            </p>
            <p v-if="this.$v.selectedDescription.$error" class="invalid-feedback d-block" style="font-size: 1rem">
                {{ $t('surgery.selection.selectedError') }}
            </p>
        </div>

        <model-field
            :class="marginClass"
            :field="surgeryFields.description"
            field-name="description"
            v-model="selectedDescription"
            @input="valueChanged"
            :errors="fieldErrors"
            :options="majorOptions"
            text-field="description">
            {{ $t('surgery.majorSurgery') }}:
        </model-field>

        <div v-if="isMajorOtherDescription">
            <input-field
                v-model="descriptionMajorOther"
                :class="marginClass"
                :errors="fieldErrors"
                :field="{label: ''}"
                :placeholder="$t('surgery.addHelp')"
                :inline="false"
                field-name="othersMajor"
                form-width="auto"
                @input="otherMajorChanged"
                no-center-text
            >
                <span></span>
            </input-field>
        </div>


        <model-field
            :class="marginClass"
            :field="surgeryFields.description"
            field-name="description"
            v-model="selectedDescription"
            @input="valueChanged"
            :options="minorOptions"
            :errors="fieldErrors"
            text-field="description">
            {{ $t('surgery.minorSurgery') }}:
        </model-field>

        <div v-if="isMinorOtherDescription">
            <input-field
                v-model="descriptionMinorOther"
                :class="marginClass"
                :errors="fieldErrors"
                :field="{label: ''}"
                :placeholder="$t('surgery.addHelp')"
                :inline="false"
                field-name="othersMinor"
                form-width="auto"
                no-center-text
                @input="otherMinorChanged"
            >
                <span></span>
            </input-field>
        </div>

        <div :class="marginClass" class="mt-4 mb-4">
            <hemocir-button
                :title="$t('surgery.addSimpleToothExtraction')"
                variant="outline-primary"
                class="w-100"
                @click="simpleExtractionClick"
            />
        </div>
    </div>
</template>

<script>
    import InputField from "@/components/fields/InputField";
    import ModelField from "@/components/fields/ModelField";
    import {mapState} from "vuex";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import SurgeryAddElementMixin from "@/components/mixins/NavigableComponentMixin";
    import {isNull} from '@/utils/formatting';
    import {
        MAJOR_SURGERY,
        MAJOR_SURGERY_ID,
        MINOR_SURGERY,
        MINOR_SURGERY_ID,
        TOOTH_EXTRACTION_ID
    } from "@/components/surgery/SurgeryUtils";
    import {required, requiredIf} from "vuelidate/lib/validators";


    export default {
        name: "TypeSelection",
        components: {
            InputField,
            ModelField,
        },
        mixins: [SurgeryAddElementMixin],
        data() {
            return {
                selectedDescription: null,
                description: {},
                descriptionMajorOther: '',
                descriptionMinorOther: '',
                generalError: ''
            };
        },
        computed: {
            ...mapState([
                'surgeryDescriptions',
                'surgeryFields',
            ]),
            ...mapStateProperty([
                'surgery.diagnosis',
                {path: 'surgery.description', name: 'surgeryDescription'},
            ]),
            majorOptions() {
                return this.surgeryDescriptions.filter(el => el.surgery_type === MAJOR_SURGERY);
            },
            minorOptions() {
                return this.surgeryDescriptions.filter(el => el.surgery_type === MINOR_SURGERY);
            },
            textDescription() {
                if (this.selectedDescription === null) { return this.$t('surgery.selection.noneSelected'); }
                if (this.isMajorOtherDescription) {
                    const option = this.surgeryDescriptions.find(el => el.id === MAJOR_SURGERY_ID);
                    return option.description + ' / ' +  this.descriptionMajorOther;
                }
                if (this.isMinorOtherDescription) {
                    const option = this.surgeryDescriptions.find(el => el.id === MINOR_SURGERY_ID);
                    return option.description + ' / ' +  this.descriptionMinorOther;
                }
                return this.description.description;
            },
            isMajorOtherDescription() {
                return this.selectedDescription === MAJOR_SURGERY_ID;
            },
            isMinorOtherDescription() {
                return this.selectedDescription === MINOR_SURGERY_ID;
            },
            marginClass() {
                return 'mx-3 mb-3';
            },
            fieldErrors() {
                let errors = {};
                if (this.$v.selectedDescription.$error) {
                    errors['description']  = [this.$t('error.required')];
                }
                if (this.$v.descriptionMajorOther.$error) {
                    errors['othersMajor']  = [this.$t('error.required')];
                }
                if (this.$v.descriptionMinorOther.$error) {
                    errors['othersMinor']  = [this.$t('error.required')];
                }
                return errors;
            }
        },
        created() {
            if (!isNull(this.surgeryDescription) && this.surgeryDescription !== '') {
                if (this.surgeryDescription.hasOwnProperty('id')) {
                    this.selectedDescription = this.surgeryDescription.id;
                } else {
                    if (this.surgeryDescription.surgery_type === MAJOR_SURGERY) {
                        this.selectedDescription = MAJOR_SURGERY_ID;
                        this.descriptionMajorOther = this.surgeryDescription.description;
                    } else if (this.surgeryDescription.surgery_type === MINOR_SURGERY) {
                        this.selectedDescription = MINOR_SURGERY_ID;
                        this.descriptionMinorOther = this.surgeryDescription.description;
                    }
                }
                this.description = this.surgeryDescription;
            }
            this.$emit('description', {
                description: '',
                surgery_type: this.$t('surgery.addSurgery'),
            });
        },
        methods: {
            valueChanged(id) {
                this.description = this.findDescription(id);
            },
            simpleExtractionClick() {
                this.description = this.findDescription(TOOTH_EXTRACTION_ID);
                this.selectedDescription = 1;
            },
            findDescription(id) {
                return this.surgeryDescriptions.find(el => el.id === id);
            },
            otherMajorChanged(description) {
                this.description = {
                    surgery_type: MAJOR_SURGERY,
                    description
                };
            },
            otherMinorChanged(description) {
                this.description = {
                    surgery_type: MINOR_SURGERY,
                    description
                };
            },
            validate() {
                this.$v.$touch();
                const isValid = !this.$v.$invalid;
                if (isValid) {
                    this.surgeryDescription = this.description;
                    this.$emit('description', this.description);
                }
                return isValid;
            },
            back() {},
        },
        validations: {
            selectedDescription: {
                required,
            },
            descriptionMajorOther: {
                required: requiredIf(function() {
                    return this.selectedDescription === MAJOR_SURGERY_ID;
                }),
            },
            descriptionMinorOther: {
                required: requiredIf(function() {
                    return this.selectedDescription === MINOR_SURGERY_ID;
                })
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import "src/custom.scss";

    .fade-enter-active, .fade-leave-active {
        transition: opacity .3s;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

</style>